import { useLocale } from '@/components/Link'
import appVariables from '@/config'
import { graphql } from '@/generated/gql/gql'
import { useMutation, useQuery } from '@apollo/client'
import { getCloudflareImageUrl } from '@fusion/media/cloudflare'
import Close from '@mui/icons-material/Close'
import StarRateRounded from '@mui/icons-material/StarRateRounded'
import {
  Avatar,
  Button,
  Card,
  CardHeader,
  CardMedia,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Rating,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import NoImageAvailable from '@public/images/no-image-available.png'

import type { Locale } from 'date-fns'
import { format } from 'date-fns/format'
import { de, enGB, nl, it } from 'date-fns/locale'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getCanteenRatingPeriod } from '../ratingDates'

const canteenItemRatingOrdersQuery = graphql(`
  query CanteenItemRating_OrdersQueryV2($input: CCUserOrderQueryInput!) {
    me {
      ccOrders(input: $input) {
        orders {
          date
          meal
          items {
            name
            id
            type
            ratedAt
            liveMenuItem {
              coreLiveItem {
                image
              }
            }
            vendor {
              id
              companyName
              logo
            }
          }
        }
      }
    }
  }
`)

const canteenItemRatingRateItemMutation = graphql(`
  mutation Mutation($input: RateCCOrderItemInput!) {
    rateCCOrderItem(input: $input)
  }
`)

interface ItemRatingMediaCardProps {
  itemName: string
  deliveryDate: string
  imageUrl?: string | null
  imageTitle?: string | null
  vendorName?: string | null
  vendorAvatar?: string | null
  locale: string
}

const localeMap: Record<string, Locale> = {
  en: enGB,
  de: de,
  nl: nl,
  it: it,
}

function ItemRatingMediaCard({
  itemName,
  deliveryDate,
  imageTitle,
  imageUrl,
  vendorName,
  vendorAvatar,
  locale,
}: ItemRatingMediaCardProps) {
  const { t } = useTranslation()
  const dateLocale = localeMap[locale.split('-')[0]] || enGB
  const deliveryDateText = t('cc:reviewDialog.deliveryOnDate', {
    deliveryDate: format(new Date(deliveryDate), 'MMMM dd, yyyy', { locale: dateLocale }),
  })

  const url = imageUrl
    ? getCloudflareImageUrl({
        enabled: appVariables.CLOUDFLARE_OPTIMISE_IMAGE,
        src: imageUrl,
        width: 50,
        quality: 100,
      })
    : NoImageAvailable.src

  const avatarUrl = getCloudflareImageUrl({
    enabled: appVariables.CLOUDFLARE_OPTIMISE_IMAGE,
    src: vendorAvatar || '',
    width: 10,
    quality: 100,
  })

  const title = imageTitle || ''
  return (
    <Card
      sx={{
        width: '100%',
        padding: 0.5,
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'divider',
        overflowWrap: 'anywhere',
      }}>
      <Stack direction="row" spacing={1} minHeight={50}>
        <CardMedia sx={{ minWidth: 50, minHeight: 50, borderRadius: 1 }} image={url} title={title}>
          {vendorAvatar && vendorName ? (
            <Stack padding={0.5}>
              <Tooltip title={vendorName}>
                <Avatar sx={{ width: 10, height: 10 }} alt={vendorName} src={avatarUrl}>
                  &nbsp;
                </Avatar>
              </Tooltip>
            </Stack>
          ) : null}
        </CardMedia>
        <Stack
          direction="column"
          justifyContent="space-between"
          width="100%"
          height="100%"
          minHeight={50}
          py={0.5}>
          <CardHeader title={<Typography>{itemName}</Typography>} sx={{ padding: 0 }} />
          <Typography variant="body3" color="text.secondary">
            {deliveryDateText}
          </Typography>
        </Stack>
      </Stack>
    </Card>
  )
}

export function ItemRatingV2() {
  const { t } = useTranslation()
  const locale = useLocale() || 'en-gb'
  const maxRating = 5
  const [closedByUser, setClosedByUser] = useState(false)
  const [{ periodStart, periodEnd }] = useState(() => getCanteenRatingPeriod())
  const [ratingValue, setRatingValue] = useState(0)
  const [moreDetails, setMoreDetails] = useState('')
  const { data } = useQuery(canteenItemRatingOrdersQuery, {
    variables: {
      input: {
        offset: 0,
        limit: 1,
        periodStart,
        periodEnd,
      },
    },
  })

  const [runSaveRatingMutation, { loading: savingRating }] = useMutation(
    canteenItemRatingRateItemMutation,
  )

  const saveRating = (input: { itemId: number; vendorId: number; skip?: boolean }) => {
    runSaveRatingMutation({
      variables: {
        input: {
          ccOrderItemId: input.itemId,
          maxRating,
          ...(moreDetails && { feedback: moreDetails }),
          rating: ratingValue,
          vendorId: input.vendorId,
          skipped: Boolean(input.skip),
        },
      },
    })
  }

  const handleClose = (input: { itemId: number; vendorId: number }) => {
    saveRating({ ...input, skip: true })
    setClosedByUser(true)
  }

  const handleSubmitRating = (input: { itemId: number; vendorId: number }) => {
    saveRating(input)
    setClosedByUser(true)
  }

  const orderData = data?.me.ccOrders.orders[0]

  const itemToReview = orderData?.items.reduce((foundItem, currentItem) => {
    const priorityMap: Record<string, number> = {
      main: 3,
      side: 2,
      drink: 1,
      '': 0,
    }
    if (foundItem.ratedAt) return foundItem
    if (!currentItem?.type) return foundItem
    const currentItemType = currentItem.type
    const foundItemType = foundItem?.type ?? ''
    const currentItemPriority = priorityMap[currentItemType]
    const foundItemPriority = priorityMap[foundItemType]
    if (currentItemPriority > foundItemPriority) return currentItem
    return foundItem
  })

  const saveInput = {
    itemId: Number(itemToReview?.id),
    vendorId: Number(itemToReview?.vendor.id),
  }

  return (
    <Dialog
      onClose={() => handleClose(saveInput)}
      maxWidth="xs"
      open={Boolean(itemToReview && !itemToReview.ratedAt && !closedByUser)}>
      <Card>
        <Stack padding={2} direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" fontWeight={'600'} data-cy="canteen-rating-modal">
            {t('cc:reviewDialog.writeAReview')}
          </Typography>
          <IconButton
            sx={{
              marginLeft: 'auto',
              border: '1px solid',
              borderColor: 'divider',
            }}
            onClick={() => handleClose(saveInput)}>
            <Close />
          </IconButton>
        </Stack>
        <DialogContent sx={{ paddingY: '8px' }}>
          <Stack spacing={2} width="100%" direction="column" alignItems={'center'}>
            <ItemRatingMediaCard
              itemName={itemToReview?.name || ''}
              deliveryDate={orderData?.date || ''}
              imageUrl={itemToReview?.liveMenuItem.coreLiveItem?.image || null}
              imageTitle={itemToReview?.name || null}
              vendorName={itemToReview?.vendor.companyName || null}
              vendorAvatar={itemToReview?.vendor.logo || null}
              locale={locale}
            />
            <Typography variant="h4" textAlign={'center'} fontWeight={'500'}>
              {t('cc:reviewDialog.howWasYourItemFromVendor', {
                itemName: itemToReview?.name,
                companyName: itemToReview?.vendor.companyName,
              })}
            </Typography>
            <Rating
              onChange={(_, newRating) => {
                if (!newRating) return
                setRatingValue(newRating)
              }}
              value={ratingValue}
              max={maxRating}
              data-cy="canteen-item-rating-stars"
              icon={<StarRateRounded fontSize="large" />}
              emptyIcon={<StarRateRounded fontSize="large" sx={{ color: 'divider' }} />}
            />
            <TextField
              placeholder={t('cc:reviewDialog.reviewPlaceholder')}
              label={t('cc:reviewDialog.review')}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { maxLength: 200 },
              }}
              multiline
              fullWidth
              minRows={5}
              value={moreDetails}
              onChange={(e) => setMoreDetails(e.target.value)}
              data-cy="canteen-item-rating-textarea"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => handleSubmitRating(saveInput)}
            disabled={ratingValue === 0 || savingRating}
            data-cy="canteen-item-rating-submit">
            {t('cc:reviewDialog.submitReview')}
          </Button>
        </DialogActions>
      </Card>
    </Dialog>
  )
}
